/* eslint-disable max-lines-per-function */
import { DownloadIcon, SearchIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Text,
  Tooltip,
  useToast
} from "@chakra-ui/react"
import React, { useState } from "react"
import { useNavigate } from "react-router"
import { Paths, breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel } from "../common/commonConstants"
import { buildDocumentUrl } from "../common/documentUrl"
import { SubPage, SubPageContent } from "../components/SubPage"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { PDFIcon } from "../components/productInfo/product-info.main"
import { Colors } from "../theme/colors"
import { useGetReportLazyQuery, useReportPageDetailsQuery } from "../type-and-hooks"

const breadcrumbOpts = {
  ...breadCrumbFirstLevel,
  ...breadCrumbReportsAndResourcesSecondLevel,
  thirdLevel: { title: "Test Reports" }
}

const RESTRICTED_REPORT_MESSAGE = "* Please contact our customer support for the report."

export const TestReports = (): JSX.Element => {
  const { data: apiData, loading, error } = useReportPageDetailsQuery({ fetchPolicy: "cache-and-network" })
  const [getReport] = useGetReportLazyQuery({ fetchPolicy: 'network-only' })
  const navigate = useNavigate()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>("")
  const toast = useToast()

  const navigateTo = (link: string): void => {
    void navigate(link)
  }

  const handlePreview = (url: string, fileName?: string | null): void => {
    window.open(buildDocumentUrl(fileName, url) ?? url, "_blank", "noopener,noreferrer")
  }

  const handleDownload = async (url: string, name: string): Promise<void> => {
    setActionLoading(`download-${url}`)
    try {
      const { data } = await getReport({ variables: { reportNo: name } })
      const downloadUrl = data?.getReport?.downloadURL ?? url
      window.open(downloadUrl, "_blank", "noopener,noreferrer")
      toast({ title: "Download started", status: "success", duration: 3000, isClosable: true })
    } catch {
      toast({ title: "Download failed", description: "Could not download the document.", status: "error", duration: 4000, isClosable: true })
    } finally {
      setActionLoading(null)
    }
  }

  const allReports = React.useMemo(() => {
    if (!apiData?.reportDetails) return []
    return apiData.reportDetails.filter((r): r is NonNullable<typeof r> => r != null)
  }, [apiData])

  const filteredTestReports = React.useMemo(() => {
    const q = searchQuery.toLowerCase()
    return allReports.filter(r => r.reportTypeId === 1 && (!q || (r.reportNo ?? "").toLowerCase().includes(q)))
  }, [allReports, searchQuery])

  const filteredAssessmentReports = React.useMemo(() => {
    const q = searchQuery.toLowerCase()
    return allReports.filter(r => (r.reportTypeId === 2 || r.reportTypeId === 6) && (!q || (r.reportNo ?? "").toLowerCase().includes(q)))
  }, [allReports, searchQuery])

  const hasRestrictedReport = React.useMemo(
    () => [...filteredTestReports, ...filteredAssessmentReports].some(r => r.isPublic === false),
    [filteredTestReports, filteredAssessmentReports]
  )

  if (loading) {
    return (
      <SubPage>
        <BreadCrumb options={breadcrumbOpts} />
        <SubPageContent innerBoxProps={{ px: "0" }}>
          <Flex align="center" justify="center" minH="400px">
            <Text>Loading test reports...</Text>
          </Flex>
        </SubPageContent>
      </SubPage>
    )
  }

  if (error) {
    return (
      <SubPage>
        <BreadCrumb options={breadcrumbOpts} />
        <SubPageContent innerBoxProps={{ px: "0" }}>
          <Flex align="center" justify="center" minH="400px">
            <Text color="red.500">Error loading test reports. Please try again later.</Text>
          </Flex>
        </SubPageContent>
      </SubPage>
    )
  }

  return (
    <>
      <SubPage>
        <BreadCrumb options={breadcrumbOpts} />
        <SubPageContent innerBoxProps={{ px: "0" }}>
          <Flex align="center" flexDir="column">
            <Box w="100%">
              {/* Intro text */}
              <Box maxW="680px" textAlign="center" margin="auto" px={[4, 4, 0]}>
                <Text variant={["RBody", "RBody", "Body1Regular"]} as="span">
                  If you are looking for a specific application, go to the relevant{" "}
                </Text>
                <Button
                  variant="linkButton"
                  fontWeight="700"
                  position="relative"
                  bottom="2px"
                  fontSize={["16px", "16px", "15px"]}
                  lineHeight={["24px", "24px", "19px"]}
                  onClick={() => { navigateTo(Paths.Product.all) }}
                >
                  Product Page
                </Button>
                <Text variant={["RBody", "RBody", "Body1Regular"]} as="span">
                  {" "}or use the{" "}
                </Text>
                <Button
                  as="a"
                  href="https://www.snapcollars.com.au/product-filter"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  borderRadius="full"
                  bg={Colors.Primary.accent}
                  color="white"
                  fontWeight="600"
                  fontSize="14px"
                  px="14px"
                  h="auto"
                  py="5px"
                  _hover={{ bg: "#96705f" }}
                  display="inline-flex"
                  verticalAlign="middle"
                  position="relative"
                  bottom="1px"
                >
                  Product Filter
                </Button>
                <Text variant={["RBody", "RBody", "Body1Regular"]} as="span" display="block" mt="10px">
                  You can access our fire collar test reports and assessments by clicking on the relevant report number
                  below. Please see individual{" "}
                  <Button
                    variant="linkButton"
                    fontWeight="700"
                    position="relative"
                    bottom="2px"
                    fontSize={["16px", "16px", "15px"]}
                    lineHeight={["24px", "24px", "19px"]}
                    onClick={() => { navigateTo(Paths.Product.all) }}
                  >
                    Product Page
                  </Button>
                  {" "}for tables of Tested Systems, Fire Resistance Levels (FRLs) and the corresponding report.
                </Text>
              </Box>

              {/* Search bar */}
              <Box maxW="500px" margin="auto" mt={["20px", "20px", "24px"]} px={[4, 4, 0]}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search test or assessments"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value) }}
                    borderRadius="md"
                    bg="white"
                    fontSize="15px"
                  />
                </InputGroup>
              </Box>

              {/* Two sections side by side */}
              <Box mt={["24px", "24px", "32px"]} px={[4, 4, 6]}>
                <SimpleGrid columns={[1, 1, 1, 2]} gap={["32px", "32px", "40px", "48px"]}>
                  <ReportSection
                    title="Test Reports"
                    reports={filteredTestReports}
                    onDownload={handleDownload}
                    onPreview={handlePreview}
                    actionLoading={actionLoading}
                  />
                  <ReportSection
                    title="Assessment Reports"
                    reports={filteredAssessmentReports}
                    onDownload={handleDownload}
                    onPreview={handlePreview}
                    actionLoading={actionLoading}
                  />
                </SimpleGrid>
                {hasRestrictedReport && (
                  <Text fontSize="12px" color={Colors.Secondary.c2} mt="16px">
                    {RESTRICTED_REPORT_MESSAGE}
                  </Text>
                )}
              </Box>
            </Box>
          </Flex>
        </SubPageContent>
      </SubPage>
    </>
  )
}

interface ReportItem {
  reportNo?: string | null
  reportURL?: string | null
  fileName?: string | null
  isPublic?: boolean | null
}

interface ReportSectionProps {
  title: string
  reports: ReportItem[]
  onDownload: (url: string, name: string) => Promise<void>
  onPreview: (url: string, fileName?: string | null) => void
  actionLoading: string | null
}

const ReportSection = ({ title, reports, onDownload, onPreview, actionLoading }: ReportSectionProps): JSX.Element => (
  <Box w="100%">
    <Text
      textAlign="center"
      fontWeight="600"
      fontSize={["18px", "18px", "20px"]}
      color={Colors.Primary.c1}
      mb="8px"
    >
      {title}
    </Text>
    <Divider mb="16px" borderColor="gray.300" />
    {reports.length === 0 ? (
      <Text color="gray.500" fontSize="14px" textAlign="center">
        No reports found.
      </Text>
    ) : (
      <SimpleGrid columns={[1, 1, 2, 3]} gap="4px" justifyItems="start">
        {reports.map((item, index) => (
          <Flex key={`${item.reportNo ?? ""}-${index}`} mt="6px" align="center" gap="4px">
            {item.isPublic === false ? (
              <>
                <Box p="0" minW="0" h="auto" visibility="hidden" aria-hidden="true">
                  <DownloadIcon />
                </Box>
                <Tooltip label={RESTRICTED_REPORT_MESSAGE} hasArrow placement="top">
                  <Flex align="center" ml="3px" cursor="default">
                    <PDFIcon />
                    <Text
                      variant={["RButton2", "RButton2", "Button2Big"]}
                      as="span"
                      paddingLeft="8px"
                      color="black"
                    >
                      {item.reportNo} *
                    </Text>
                  </Flex>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  variant="linkButton"
                  p="0"
                  minW="0"
                  h="auto"
                  display="flex"
                  alignItems="center"
                  flexShrink={0}
                  onClick={() => { if (item.reportURL) void onDownload(item.reportURL, item.reportNo ?? "") }}
                  isLoading={actionLoading === `download-${item.reportURL}`}
                  aria-label={`Download ${item.reportNo ?? ""}`}
                >
                  <DownloadIcon />
                </Button>
                <Button
                  variant="linkButton"
                  ml="3px"
                  minH="22px"
                  fontSize="15px"
                  textAlign="left"
                  lineHeight="18px"
                  fontWeight="400"
                  whiteSpace="unset"
                  wordBreak="break-word"
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  color={Colors.Primary.c1}
                  _hover={{ color: Colors.Primary.c1, textDecoration: "underline" }}
                  onClick={() => { if (item.reportURL) onPreview(item.reportURL, item.fileName) }}
                >
                  <PDFIcon />
                  <Text
                    variant={["RButton2", "RButton2", "Button2Big"]}
                    as="span"
                    paddingLeft="8px"
                    color={Colors.Primary.c1}
                  >
                    {item.reportNo}
                  </Text>
                </Button>
              </>
            )}
          </Flex>
        ))}
      </SimpleGrid>
    )}
  </Box>
)
